import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, ChevronDown, ChevronUp, Loader2, MessageSquare, Send, Sparkles, ThumbsUp } from 'lucide-react';
import { useSmartBack } from '../hooks/useSmartBack';
import { roadmapApi, roadmapErrorMessage, roadmapErrorStatus } from '../services/roadmapApi';
import type { RoadmapComment, RoadmapItem } from '../services/roadmapApi';

// Funciones futuras (2026-10-13, Jaime-approved roadmap MVP): in-app page
// where any logged-in user sees the public feature roadmap, votes (one vote
// per user, POST /:id/vote is a toggle) and comments per item (1..2000 chars,
// rate-limit friendly errors). Author display is author_name only — never
// emails or ids. Spanish-first copy. Comments are collapsed per card behind a
// "Ver comentarios (N)" disclosure (Jaime directive 2026-10-13 13:44 UTC) and
// lazy-load on first unfold; vote toggle behavior untouched.
//
// 503 FEATURE_ROADMAP_UNAVAILABLE (migration 161/162 not applied) degrades to
// an inline error state with retry; the page itself never breaks.

const STATUS_LABELS: Record<string, string> = {
  planned: 'Planificado',
  in_progress: 'En desarrollo',
  shipped: 'Lanzado',
};

const MAX_COMMENT_CHARS = 2000;
const RATE_LIMIT_MESSAGE = 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.';
const LOAD_FALLBACK = 'No se pudo cargar el roadmap.';
const COMMENT_FALLBACK = 'No se pudo publicar el comentario.';

const dateFmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });

function formatCommentDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d);
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'shipped': return 'bg-emerald-50 text-emerald-600 border border-emerald-200';
    case 'in_progress': return 'bg-amber-50 text-amber-600 border border-amber-200';
    default: return 'bg-blue-50 text-blue-600 border border-blue-200';
  }
}

export default function FuncionesFuturas() {
  const goBack = useSmartBack('/home/dashboard');

  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [myVotes, setMyVotes] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [commentsByItem, setCommentsByItem] = useState<Record<string, RoadmapComment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState<Set<string>>(() => new Set());
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sendingIds, setSendingIds] = useState<Set<string>>(() => new Set());
  const [commentErrors, setCommentErrors] = useState<Record<string, string>>({});

  const [votingIds, setVotingIds] = useState<Set<string>>(() => new Set());
  const [voteErrors, setVoteErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [list, votes] = await Promise.all([roadmapApi.list(), roadmapApi.myVotes().catch(() => [] as string[])]);
      setItems(list);
      setMyVotes(new Set(votes));
    } catch (e) {
      const status = roadmapErrorStatus(e);
      setLoadError(status === 503
        ? 'El roadmap no está disponible en este momento. Inténtalo más tarde.'
        : roadmapErrorMessage(e, LOAD_FALLBACK));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleVote(item: RoadmapItem) {
    if (votingIds.has(item.id)) return;
    setVoteErrors((prev) => ({ ...prev, [item.id]: '' }));
    setVotingIds((prev) => new Set(prev).add(item.id));
    try {
      const r = await roadmapApi.vote(item.id);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, vote_count: r.vote_count } : i)));
      setMyVotes((prev) => {
        const next = new Set(prev);
        if (r.voted) next.add(item.id); else next.delete(item.id);
        return next;
      });
    } catch (e) {
      const status = roadmapErrorStatus(e);
      if (status === 404) {
        // The item became non-votable (shipped/declined/unpublished) — refresh.
        try { setItems(await roadmapApi.list()); } catch { /* keep current list */ }
      } else if (status === 429) {
        setVoteErrors((prev) => ({ ...prev, [item.id]: RATE_LIMIT_MESSAGE }));
      } else {
        setVoteErrors((prev) => ({ ...prev, [item.id]: roadmapErrorMessage(e, 'No se pudo registrar tu voto.') }));
      }
    } finally {
      setVotingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function toggleComments(item: RoadmapItem) {
    const isOpen = expandedIds.has(item.id);
    setCommentErrors((prev) => ({ ...prev, [item.id]: '' }));
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(item.id); else next.add(item.id);
      return next;
    });
    if (isOpen || commentsByItem[item.id]) return;
    setCommentsLoading((prev) => new Set(prev).add(item.id));
    try {
      const rows = await roadmapApi.comments(item.id);
      setCommentsByItem((prev) => ({ ...prev, [item.id]: rows }));
    } catch (e) {
      const status = roadmapErrorStatus(e);
      if (status === 404) {
        try { setItems(await roadmapApi.list()); } catch { /* keep current list */ }
      } else {
        setCommentErrors((prev) => ({ ...prev, [item.id]: status === 429 ? RATE_LIMIT_MESSAGE : roadmapErrorMessage(e, 'No se pudieron cargar los comentarios.') }));
      }
    } finally {
      setCommentsLoading((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function submitComment(item: RoadmapItem) {
    const body = (drafts[item.id] || '').trim();
    if (!body || body.length > MAX_COMMENT_CHARS || sendingIds.has(item.id)) return;
    setCommentErrors((prev) => ({ ...prev, [item.id]: '' }));
    setSendingIds((prev) => new Set(prev).add(item.id));
    try {
      const created = await roadmapApi.addComment(item.id, body);
      setCommentsByItem((prev) => ({ ...prev, [item.id]: [created, ...(prev[item.id] || [])] }));
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, comment_count: i.comment_count + 1 } : i)));
      setDrafts((prev) => ({ ...prev, [item.id]: '' }));
    } catch (e) {
      const status = roadmapErrorStatus(e);
      setCommentErrors((prev) => ({
        ...prev,
        [item.id]: status === 429
          ? RATE_LIMIT_MESSAGE
          : roadmapErrorMessage(e, COMMENT_FALLBACK),
      }));
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  const draftLen = (id: string) => (drafts[id] || '').length;

  return (
    <div className="min-h-[calc(var(--vvh,100%)_+_env(safe-area-inset-top))] bg-gray-50 dark:bg-[var(--v-surface-1)]" style={{ fontFamily: 'var(--v-font-sans)' }}>
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white dark:bg-[var(--v-surface-1)] border-b border-gray-100 dark:border-[var(--v-border-light)]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={goBack}
            aria-label="Volver"
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:text-[var(--v-teal-700)] hover:bg-[var(--v-teal-400)]/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 text-[var(--v-teal-700)] shrink-0" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">Funciones futuras</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Lo que estamos construyendo para tu condominio. Vota por lo que quieres ver primero y cuéntanos qué opinas.
        </p>

        {loading && (
          <div className="py-20 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[var(--v-teal-700)]" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Cargando roadmap…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className="py-16 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">{loadError}</p>
            <button
              onClick={load}
              className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[var(--v-teal-700)] hover:bg-[var(--v-teal-800)] rounded-full transition-colors"
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && !loadError && items.length === 0 && (
          <div className="py-16 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-gray-300" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Aún no hay funciones publicadas en el roadmap.</p>
          </div>
        )}

        {!loading && !loadError && items.length > 0 && (
          <div className="space-y-4">
            {items.map((item) => {
              const voted = myVotes.has(item.id);
              const expanded = expandedIds.has(item.id);
              const rows = commentsByItem[item.id] || [];
              const draft = drafts[item.id] || '';
              return (
                <article key={item.id} className="bg-white dark:bg-[var(--v-surface-2)] rounded-2xl border border-gray-100 dark:border-[var(--v-border-light)] shadow-sm p-5">
                  {/* Badges row: area + status + eta */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {item.area && (
                      <span className="text-xs font-semibold text-[var(--v-teal-800)] bg-[var(--v-teal-400)]/10 px-2.5 py-1 rounded-full">{item.area}</span>
                    )}
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadgeClass(item.status)}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                    {item.eta_label && <span className="text-xs text-gray-400 dark:text-gray-500">{item.eta_label}</span>}
                  </div>

                  <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1.5">{item.title}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">{item.description}</p>

                  {item.credit_label && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{item.credit_label}</p>
                  )}

                  {/* Actions row: vote + comments toggle */}
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={() => toggleVote(item)}
                      disabled={votingIds.has(item.id)}
                      aria-pressed={voted}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all disabled:opacity-60 ${
                        voted
                          ? 'bg-[var(--v-teal-700)] text-white hover:bg-[var(--v-teal-800)]'
                          : 'bg-[var(--v-teal-400)]/10 text-[var(--v-teal-800)] hover:bg-[var(--v-teal-400)]/20 border border-[var(--v-teal-400)]/30'
                      }`}
                    >
                      {votingIds.has(item.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                      {voted ? 'Votado' : 'Votar'}
                      <span className="tabular-nums">· {item.vote_count}</span>
                    </button>
                    <button
                      onClick={() => toggleComments(item)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[var(--v-surface-3)] transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {/* Jaime directive 2026-10-13 13:44 UTC: readable copy on the
                          disclosure toggle — collapsed invites reading, expanded offers hide. */}
                      {expanded ? `Ocultar comentarios (${item.comment_count})` : `Ver comentarios (${item.comment_count})`}
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                  {voteErrors[item.id] && (
                    <p className="text-xs text-red-500 mt-2" aria-live="polite">{voteErrors[item.id]}</p>
                  )}

                  {/* Expandable comment section */}
                  {expanded && (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[var(--v-border-light)]">
                      {commentsLoading.has(item.id) ? (
                        <div className="py-6 text-center">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                          <p className="text-xs text-gray-400 mt-2">Cargando comentarios…</p>
                        </div>
                      ) : (
                        <>
                          {rows.length === 0 ? (
                            <p className="text-xs text-gray-400 py-3">Sé el primero en comentar esta función.</p>
                          ) : (
                            <ul className="space-y-3 mb-4">
                              {rows.map((c) => (
                                <li key={c.id} className="flex gap-3">
                                  <div className="w-8 h-8 shrink-0 rounded-full bg-[var(--v-teal-400)]/15 text-[var(--v-teal-800)] flex items-center justify-center text-xs font-bold">
                                    {(c.author_name || 'U').trim().charAt(0).toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      <span className="font-semibold text-gray-700 dark:text-gray-200">{c.author_name || 'Usuario'}</span>
                                      {c.created_at ? ` · ${formatCommentDate(c.created_at)}` : ''}
                                    </p>
                                    <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line break-words">{c.body}</p>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* Composer: 1..2000 chars after trim (backend contract) */}
                          <div className="flex items-start gap-2">
                            <textarea
                              value={draft}
                              onChange={(e) => setDrafts((prev) => ({ ...prev, [item.id]: e.target.value.slice(0, MAX_COMMENT_CHARS) }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitComment(item);
                              }}
                              placeholder="Escribe un comentario…"
                              rows={2}
                              maxLength={MAX_COMMENT_CHARS}
                              className="flex-1 resize-none text-sm border border-gray-200 dark:border-[var(--v-border-light)] rounded-xl px-3 py-2 bg-gray-50 dark:bg-[var(--v-surface-1)] text-gray-900 dark:text-white focus:ring-2 focus:ring-[var(--v-teal-400)] focus:outline-none"
                            />
                            <button
                              onClick={() => submitComment(item)}
                              disabled={!draft.trim() || sendingIds.has(item.id)}
                              aria-label="Publicar comentario"
                              className="shrink-0 p-2.5 rounded-xl text-white bg-[var(--v-teal-700)] hover:bg-[var(--v-teal-800)] disabled:opacity-50 transition-colors"
                            >
                              {sendingIds.has(item.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-[11px] ${draftLen(item.id) > MAX_COMMENT_CHARS - 200 ? 'text-amber-500' : 'text-gray-400'}`}>{draftLen(item.id)}/{MAX_COMMENT_CHARS}</span>
                            <span className="text-[11px] text-gray-400">Ctrl+Enter para publicar</span>
                          </div>
                          {commentErrors[item.id] && (
                            <p className="text-xs text-red-500 mt-1" aria-live="polite">{commentErrors[item.id]}</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
